/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { useToast } from "@app/vue/providers/index";
import { Avatar } from "../shared/Avatar";
import type { Member } from "./types";

type Props = { member: Member; url: string; emailed: boolean; onClose: () => void };

// Shown once after a link is made: the URL (only now — we keep a hash), to copy or share.
export const LinkModal = defineComponent<Props>({
  name: "LinkModal",
  props: ["member", "url", "emailed", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const toast = useToast();
    const copied = ref(false);

    const copy = async () => {
      try {
        await navigator.clipboard.writeText(props.url);
        copied.value = true;
      } catch {
        toast.showToast(t("inventory.shopping.copy_failed"), { type: "error" });
      }
    };

    return () => (
      <LemmonModal
        title={t("workday.team.link_title")}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="border-t p-4 text-right">
            <button class="btn btn-primary" onClick={() => props.onClose()}>
              {t("workday.common.done")}
            </button>
          </div>
        )}
      >
        <div class="space-y-4 p-6">
          <div class="flex items-center gap-3">
            <Avatar name={props.member.name} id={props.member.id} color={props.member.color} size="md" />
            <div>
              <div class="font-semibold">{props.member.name}</div>
              <div class="text-sm text-base-content/60">
                {props.emailed
                  ? t("workday.team.link_emailed").replace("{email}", props.member.email ?? "")
                  : t("workday.team.link_not_emailed")}
              </div>
            </div>
          </div>
          <div class="join w-full">
            <input class="input join-item input-bordered input-sm w-full font-mono text-xs" readonly value={props.url} onFocus={(e: FocusEvent) => (e.target as HTMLInputElement).select()} />
            <button class={["btn join-item btn-sm", copied.value ? "btn-success" : "btn-neutral"].join(" ")} onClick={copy}>
              <span class="material-symbols-outlined text-base">{copied.value ? "check" : "content_copy"}</span>
              {copied.value ? t("workday.team.copied") : t("workday.team.copy")}
            </button>
          </div>
          <div class="flex gap-2">
            <a class="btn btn-ghost btn-sm" target="_blank" rel="noopener" href={`https://wa.me/?text=${encodeURIComponent(props.url)}`}>
              <span class="material-symbols-outlined text-base">chat</span>
              WhatsApp
            </a>
          </div>
          <p class="text-xs text-base-content/60">{t("workday.team.link_once")}</p>
        </div>
      </LemmonModal>
    );
  },
});
